import { useFeedbacksControllerCreateMutation } from '@app/api/feedbackApi/feedback-api';
import { useFeedbacksControllerFindOneMutation } from '@app/api/tagged/feedback-api';
import { Button, LinkButton, Popover, OVERLAY_ANIMATION_DURATION } from '@app/components';
import { SiteMap, UrlQueryParam } from '@app/constants';
import { useCustomerQuery } from '@app/features/account';
import { cleanPhone } from '@app/features/auth';
import {
  COMMON_FORM_FIELDS_SCHEMA,
  CommonFormFieldName,
  CommonFormFields,
  POLICY_AGREEMENT_SCHEMA,
  PolicyAgreement,
  formatPhone,
} from '@app/features/common-form-fields';
import { useEnhancedTranslation } from '@app/hooks';
import { EventRegistrationType, TEvent } from '@app/models';
import { Form, Formik } from 'formik';
import { FC, useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, createSearchParams } from 'react-router-dom';

import './registration-form.scss';

type EventRegistrationFormProps = {
  event: TEvent;
  open: boolean;
  onClose: () => void;
};

export const EventRegistrationForm: FC<EventRegistrationFormProps> = ({ event, open, onClose }) => {
  const { t, tRoot, tMap } = useEnhancedTranslation({ keyPrefix: 'events.regForm' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const {
    data: customer,
    isSuccess: customerLoaded,
    isError: isLoggedOut,
    isLoading: isCustomerLoading,
  } = useCustomerQuery();

  const [send] = useFeedbacksControllerCreateMutation();
  const [sendLoggedIn] = useFeedbacksControllerFindOneMutation();

  const initialValues = useMemo(
    () => ({
      [CommonFormFieldName.FIRST_NAME]: customer ? customer.firstName : '',
      [CommonFormFieldName.LAST_NAME]: customer ? customer.lastName : '',
      [CommonFormFieldName.MIDDLE_NAME]: customer ? customer.middleName : '',
      [CommonFormFieldName.EMAIL]: customer?.email ? customer.email : '',
      [CommonFormFieldName.PHONE]: customer ? formatPhone(customer.phone) : '',
      [CommonFormFieldName.POLICY_AGREEMENT]: false,
    }),
    [customer],
  );

  const handleClose = useCallback(() => {
    onClose();
    if (submitted) {
      setTimeout(() => setSubmitted(false), OVERLAY_ANIMATION_DURATION);
    }
  }, [onClose, submitted]);

  const onSubmit = useCallback(
    async (values: typeof initialValues) => {
      let resp;
      if (customer) {
        resp = await sendLoggedIn({
          shortId: event.id,
          phone: cleanPhone(values.phone),
        });
      } else {
        resp = await send({
          feedbackDto: {
            ...values,
            brandId: event.id,
            type: 'reservation',
            text: '',
            createAt: new Date().toISOString(),
          },
        });
      }
      setError('error' in resp);
      setSubmitted(true);
    },
    [customer, event.id, send, sendLoggedIn],
  );

  const loginLink = useMemo(
    () =>
      `${SiteMap.LOGIN}?${createSearchParams({
        [UrlQueryParam.REDIRECT_URL]: window.location.href,
      })}`,
    [],
  );

  const regFormContent = useMemo(
    () => (
      <>
        <h2 className="event-reg-form__title">{t('title')}</h2>
        <p className="event-reg-form__details">{tMap(event.name)}</p>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={COMMON_FORM_FIELDS_SCHEMA.concat(POLICY_AGREEMENT_SCHEMA)}
        >
          {({ dirty, errors, isValid }) => (
            <Form>
              <CommonFormFields
                className="event-reg-form__common-fields"
                labelClassName="event-reg-form__label"
                readOnly={
                  customer
                    ? {
                        [CommonFormFieldName.FIRST_NAME]: true,
                        [CommonFormFieldName.LAST_NAME]: true,
                        [CommonFormFieldName.MIDDLE_NAME]: true,
                        [CommonFormFieldName.EMAIL]: !!customer.email,
                        [CommonFormFieldName.PHONE]: true,
                      }
                    : false
                }
                errors={errors}
              />
              {!customer && (
                <p className="event-reg-form__auth-warning">
                  {/* eslint-disable-next-line react/jsx-key */}
                  <Trans t={t} i18nKey="authWarning" components={[<Link to={loginLink} />]} />
                </p>
              )}
              <PolicyAgreement className="event-reg-form__accept-policy" />
              <Button disabled={!isValid || !dirty} className="event-reg-form__submit-btn" type="submit">
                {tRoot('common.form.send')}
              </Button>
            </Form>
          )}
        </Formik>
      </>
    ),
    [customer, event.name, initialValues, loginLink, onSubmit, t, tMap, tRoot],
  );

  const vipContent = useMemo(
    () => (
      <>
        <h2 className="event-reg-form__title" style={{ textAlign: 'center' }}>
          {t('vipOnly')}
        </h2>
        <LinkButton className="event-reg-form__submit-btn" to={loginLink} onClick={onClose}>
          {t('logIn')}
        </LinkButton>
      </>
    ),
    [loginLink, onClose, t],
  );

  const submittedContent = useMemo(
    () => (
      <>
        <h2 className="event-reg-form__title">{t(error ? 'error' : 'success')}</h2>
        <Button className="event-reg-form__submit-btn" onClick={handleClose}>
          {tRoot('common.close')}
        </Button>
      </>
    ),
    [error, handleClose, t, tRoot],
  );

  const popoverContent = useMemo(() => {
    if (submitted) {
      return submittedContent;
    }
    if (isCustomerLoading) {
      return null; // TODO add loader;
    }
    if (isLoggedOut && event.registrationType === EventRegistrationType.VIP) {
      return vipContent;
    }
    if (customerLoaded || isLoggedOut) {
      return regFormContent;
    }
    return null;
  }, [
    customerLoaded,
    event.registrationType,
    isCustomerLoading,
    isLoggedOut,
    regFormContent,
    submitted,
    submittedContent,
    vipContent,
  ]);

  return (
    <Popover className="event-reg-form" open={open} onClose={handleClose}>
      {popoverContent}
    </Popover>
  );
};
